import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  Users, 
  UserPlus, 
  Search, 
  Edit, 
  Trash2, 
  Key, 
  Shield,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  Copy,
  RefreshCw,
  Building2,
  Mail,
  Phone,
  Calendar,
  MapPin,
  MoreVertical,
  UserCog
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

function EmployeeManagement() {
  var loadingState = React.useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var employeesState = React.useState([]);
  var employees = employeesState[0];
  var setEmployees = employeesState[1];
  
  var statsState = React.useState({ total: 0, active: 0, inactive: 0 });
  var stats = statsState[0];
  var setStats = statsState[1];
  
  var rolesState = React.useState([]);
  var roles = rolesState[0];
  var setRoles = rolesState[1];
  
  var departmentsState = React.useState([]);
  var departments = departmentsState[0];
  var setDepartments = departmentsState[1];
  
  var searchState = React.useState('');
  var search = searchState[0];
  var setSearch = searchState[1];
  
  var filterRoleState = React.useState('');
  var filterRole = filterRoleState[0];
  var setFilterRole = filterRoleState[1];
  
  var filterDeptState = React.useState('');
  var filterDept = filterDeptState[0];
  var setFilterDept = filterDeptState[1];
  
  var showModalState = React.useState(false);
  var showModal = showModalState[0];
  var setShowModal = showModalState[1];
  
  var editingState = React.useState(null);
  var editing = editingState[0];
  var setEditing = editingState[1];
  
  var savingState = React.useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];
  
  var showPasswordState = React.useState(false);
  var showPassword = showPasswordState[0];
  var setShowPassword = showPasswordState[1];
  
  var tempPasswordState = React.useState(null);
  var tempPassword = tempPasswordState[0];
  var setTempPassword = tempPasswordState[1];
  
  var formState = React.useState({
    full_name: '',
    mobile: '',
    email: '',
    department: '',
    role: '',
    branch: 'Main Warehouse',
    address: '',
    joining_date: '',
    password: ''
  });
  var form = formState[0];
  var setForm = formState[1];

  React.useEffect(function() {
    fetchEmployees();
    fetchRoles();
  }, []);

  function fetchEmployees() {
    setLoading(true);
    var token = localStorage.getItem('token');
    axios.get(API + '/admin/employees', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setEmployees(response.data.employees || []);
      setStats(response.data.stats || { total: 0, active: 0, inactive: 0 });
    }).catch(function() {
      toast.error('Failed to load employees');
    }).finally(function() {
      setLoading(false);
    });
  }

  function fetchRoles() {
    var token = localStorage.getItem('token');
    axios.get(API + '/admin/employee-roles', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setRoles(response.data.roles || []);
      setDepartments(response.data.departments || []);
    }).catch(function() {
      // Roles load failed — dropdowns will be empty, caller can retry.
    });
  }

  function openCreateModal() {
    setEditing(null);
    setForm({
      full_name: '',
      mobile: '',
      email: '',
      department: '',
      role: 'viewer',
      branch: 'Main Warehouse',
      address: '',
      joining_date: new Date().toISOString().split('T')[0],
      password: ''
    });
    setTempPassword(null);
    setShowModal(true);
  }

  function openEditModal(emp) {
    setEditing(emp);
    setForm({
      full_name: emp.full_name || '',
      mobile: emp.mobile || '',
      email: emp.email || '',
      department: emp.department || '',
      role: emp.role || 'viewer',
      branch: emp.branch || 'Main Warehouse',
      address: emp.address || '',
      joining_date: emp.joining_date || '',
      password: ''
    });
    setTempPassword(null);
    setShowModal(true);
  }

  function saveEmployee() {
    if (!form.full_name.trim() || !form.email.trim() || !form.department || !form.role) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    var token = localStorage.getItem('token');

    if (editing) {
      // Update existing
      axios.put(API + '/admin/employees/' + editing.id, {
        full_name: form.full_name,
        mobile: form.mobile,
        department: form.department,
        role: form.role,
        branch: form.branch,
        address: form.address,
        joining_date: form.joining_date
      }, {
        headers: { Authorization: 'Bearer ' + token }
      }).then(function() {
        toast.success('Employee updated successfully');
        setShowModal(false);
        fetchEmployees();
      }).catch(function(err) {
        toast.error(err.response?.data?.detail || 'Failed to update employee');
      }).finally(function() {
        setSaving(false);
      });
    } else {
      // Create new
      axios.post(API + '/admin/employees', form, {
        headers: { Authorization: 'Bearer ' + token }
      }).then(function(response) {
        toast.success('Employee created successfully');
        if (response.data.temp_password) {
          setTempPassword(response.data.temp_password);
        } else {
          setShowModal(false);
        }
        fetchEmployees();
      }).catch(function(err) {
        toast.error(err.response?.data?.detail || 'Failed to create employee');
      }).finally(function() {
        setSaving(false);
      });
    }
  }

  function resetPassword(emp) {
    if (!window.confirm('Reset password for ' + emp.full_name + '?')) return;
    
    var token = localStorage.getItem('token');
    axios.post(API + '/admin/employees/' + emp.id + '/reset-password', {}, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      if (response.data.temp_password) {
        toast.success('Password reset. New password: ' + response.data.temp_password);
        // Also copy to clipboard
        navigator.clipboard.writeText(response.data.temp_password);
        toast.info('Password copied to clipboard');
      } else {
        toast.success('Password reset successfully');
      }
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    });
  }

  function toggleStatus(emp, newStatus) {
    var token = localStorage.getItem('token');
    axios.put(API + '/admin/employees/' + emp.id + '/status?status=' + newStatus, {}, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Employee ' + (newStatus === 'active' ? 'activated' : 'deactivated'));
      fetchEmployees();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    });
  }

  function deleteEmployee(emp) {
    if (!window.confirm('Deactivate ' + emp.full_name + '? They will not be able to login.')) return;
    
    var token = localStorage.getItem('token');
    axios.delete(API + '/admin/employees/' + emp.id, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function() {
      toast.success('Employee deactivated');
      fetchEmployees();
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate employee');
    });
  }

  function copyToClipboard(text) {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  }

  function getRoleBadgeColor(role) {
    var colors = {
      admin: 'bg-red-100 text-red-700',
      procurement_manager: 'bg-blue-100 text-blue-700',
      warehouse_grn: 'bg-green-100 text-green-700',
      sales_executive: 'bg-purple-100 text-purple-700',
      accounts_team: 'bg-orange-100 text-orange-700',
      viewer: 'bg-gray-100 text-gray-700'
    };
    return colors[role] || 'bg-gray-100 text-gray-700';
  }

  function getRoleDisplayName(role) {
    var r = roles.find(function(x) { return x.id === role; });
    return r ? r.name : role;
  }

  // Filter employees
  var filteredEmployees = employees.filter(function(emp) {
    var matchesSearch = !search || 
      emp.full_name.toLowerCase().includes(search.toLowerCase()) ||
      emp.email.toLowerCase().includes(search.toLowerCase()) ||
      emp.employee_id.toLowerCase().includes(search.toLowerCase());
    var matchesRole = !filterRole || emp.role === filterRole;
    var matchesDept = !filterDept || emp.department === filterDept;
    return matchesSearch && matchesRole && matchesDept;
  });

  if (loading) {
    return React.createElement('div', { className: 'flex items-center justify-center py-12' },
      React.createElement(Loader2, { className: 'w-8 h-8 animate-spin text-blue-600' })
    );
  }

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'employee-management' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('div', null,
        React.createElement('h2', { className: 'text-2xl font-bold flex items-center gap-2' },
          React.createElement(UserCog, { className: 'w-6 h-6 text-blue-600' }),
          'Employee Management'
        ),
        React.createElement('p', { className: 'text-gray-500 text-sm mt-1' },
          'Manage ERP users, roles, and access permissions'
        )
      ),
      React.createElement('div', { className: 'flex gap-2' },
        React.createElement(Button, { variant: 'outline', onClick: fetchEmployees, className: 'gap-2' },
          React.createElement(RefreshCw, { className: 'w-4 h-4' }),
          'Refresh'
        ),
        React.createElement(Button, { onClick: openCreateModal, className: 'gap-2 bg-green-600 hover:bg-green-700', 'data-testid': 'create-employee-btn' },
          React.createElement(UserPlus, { className: 'w-4 h-4' }),
          'Add Employee'
        )
      )
    ),
    
    // Stats Cards
    React.createElement('div', { className: 'grid grid-cols-3 gap-4' },
      React.createElement(Card, { className: 'bg-blue-50' },
        React.createElement(CardContent, { className: 'p-4 flex items-center gap-3' },
          React.createElement(Users, { className: 'w-8 h-8 text-blue-600' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'text-2xl font-bold text-blue-700' }, stats.total),
            React.createElement('p', { className: 'text-sm text-blue-600' }, 'Total Employees')
          )
        )
      ),
      React.createElement(Card, { className: 'bg-green-50' },
        React.createElement(CardContent, { className: 'p-4 flex items-center gap-3' },
          React.createElement(CheckCircle2, { className: 'w-8 h-8 text-green-600' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'text-2xl font-bold text-green-700' }, stats.active),
            React.createElement('p', { className: 'text-sm text-green-600' }, 'Active')
          )
        )
      ),
      React.createElement(Card, { className: 'bg-red-50' },
        React.createElement(CardContent, { className: 'p-4 flex items-center gap-3' },
          React.createElement(XCircle, { className: 'w-8 h-8 text-red-600' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'text-2xl font-bold text-red-700' }, stats.inactive),
            React.createElement('p', { className: 'text-sm text-red-600' }, 'Inactive')
          )
        )
      )
    ),
    
    // Search & Filters
    React.createElement(Card, null,
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex gap-4 flex-wrap' },
          React.createElement('div', { className: 'flex-1 min-w-[200px]' },
            React.createElement('div', { className: 'relative' },
              React.createElement(Search, { className: 'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' }),
              React.createElement(Input, {
                placeholder: 'Search by name, email, or ID...',
                value: search,
                onChange: function(e) { setSearch(e.target.value); },
                className: 'pl-10'
              })
            )
          ),
          React.createElement('div', { className: 'w-[180px]' },
            React.createElement(Select, { value: filterRole || 'all', onValueChange: function(v) { setFilterRole(v === 'all' ? '' : v); } },
              React.createElement(SelectTrigger, null,
                React.createElement(SelectValue, { placeholder: 'All Roles' })
              ),
              React.createElement(SelectContent, null,
                React.createElement(SelectItem, { value: 'all' }, 'All Roles'),
                roles.map(function(r) {
                  return React.createElement(SelectItem, { key: r.id, value: r.id }, r.name);
                })
              )
            )
          ),
          React.createElement('div', { className: 'w-[180px]' },
            React.createElement(Select, { value: filterDept || 'all', onValueChange: function(v) { setFilterDept(v === 'all' ? '' : v); } },
              React.createElement(SelectTrigger, null,
                React.createElement(SelectValue, { placeholder: 'All Departments' })
              ),
              React.createElement(SelectContent, null,
                React.createElement(SelectItem, { value: 'all' }, 'All Departments'),
                departments.map(function(d) {
                  return React.createElement(SelectItem, { key: d, value: d }, d);
                })
              )
            )
          )
        )
      )
    ),
    
    // Employees Table
    React.createElement(Card, null,
      React.createElement(CardContent, { className: 'p-0' },
        React.createElement('div', { className: 'overflow-auto' },
          React.createElement('table', { className: 'w-full text-sm' },
            React.createElement('thead', { className: 'bg-slate-100' },
              React.createElement('tr', null,
                React.createElement('th', { className: 'text-left p-3' }, 'Employee'),
                React.createElement('th', { className: 'text-left p-3' }, 'Contact'),
                React.createElement('th', { className: 'text-left p-3' }, 'Department'),
                React.createElement('th', { className: 'text-left p-3' }, 'Role'),
                React.createElement('th', { className: 'text-center p-3' }, 'Status'),
                React.createElement('th', { className: 'text-left p-3' }, 'Last Login'),
                React.createElement('th', { className: 'text-center p-3' }, 'Actions')
              )
            ),
            React.createElement('tbody', null,
              filteredEmployees.length === 0
                ? React.createElement('tr', null,
                    React.createElement('td', { colSpan: 7, className: 'text-center py-8 text-gray-500' },
                      'No employees found'
                    )
                  )
                : filteredEmployees.map(function(emp) {
                    return React.createElement('tr', { key: emp.id, className: 'border-b hover:bg-slate-50' },
                      // Employee Info
                      React.createElement('td', { className: 'p-3' },
                        React.createElement('div', null,
                          React.createElement('p', { className: 'font-medium' }, emp.full_name),
                          React.createElement('p', { className: 'text-xs text-gray-500 font-mono' }, emp.employee_id)
                        )
                      ),
                      // Contact
                      React.createElement('td', { className: 'p-3' },
                        React.createElement('div', { className: 'text-xs' },
                          React.createElement('p', { className: 'flex items-center gap-1' },
                            React.createElement(Mail, { className: 'w-3 h-3' }),
                            emp.email
                          ),
                          emp.mobile && React.createElement('p', { className: 'flex items-center gap-1 mt-1' },
                            React.createElement(Phone, { className: 'w-3 h-3' }),
                            emp.mobile
                          )
                        )
                      ),
                      // Department
                      React.createElement('td', { className: 'p-3' },
                        React.createElement('span', { className: 'text-sm' }, emp.department)
                      ),
                      // Role
                      React.createElement('td', { className: 'p-3' },
                        React.createElement(Badge, { className: getRoleBadgeColor(emp.role) },
                          getRoleDisplayName(emp.role)
                        )
                      ),
                      // Status
                      React.createElement('td', { className: 'p-3 text-center' },
                        emp.status === 'active'
                          ? React.createElement(Badge, { className: 'bg-green-100 text-green-700' }, 'Active')
                          : React.createElement(Badge, { variant: 'destructive' }, emp.status || 'Inactive')
                      ),
                      // Last Login
                      React.createElement('td', { className: 'p-3 text-xs text-gray-500' },
                        emp.last_login 
                          ? new Date(emp.last_login).toLocaleString()
                          : 'Never'
                      ),
                      // Actions
                      React.createElement('td', { className: 'p-3 text-center' },
                        React.createElement(DropdownMenu, null,
                          React.createElement(DropdownMenuTrigger, { asChild: true },
                            React.createElement(Button, { variant: 'ghost', size: 'sm' },
                              React.createElement(MoreVertical, { className: 'w-4 h-4' })
                            )
                          ),
                          React.createElement(DropdownMenuContent, { align: 'end' },
                            React.createElement(DropdownMenuItem, { onClick: function() { openEditModal(emp); } },
                              React.createElement(Edit, { className: 'w-4 h-4 mr-2' }),
                              'Edit Details'
                            ),
                            React.createElement(DropdownMenuItem, { onClick: function() { resetPassword(emp); } },
                              React.createElement(Key, { className: 'w-4 h-4 mr-2' }),
                              'Reset Password'
                            ),
                            emp.status === 'active'
                              ? React.createElement(DropdownMenuItem, { onClick: function() { toggleStatus(emp, 'inactive'); }, className: 'text-red-600' },
                                  React.createElement(XCircle, { className: 'w-4 h-4 mr-2' }),
                                  'Deactivate'
                                )
                              : React.createElement(DropdownMenuItem, { onClick: function() { toggleStatus(emp, 'active'); }, className: 'text-green-600' },
                                  React.createElement(CheckCircle2, { className: 'w-4 h-4 mr-2' }),
                                  'Activate'
                                )
                          )
                        )
                      )
                    );
                  })
            )
          )
        )
      )
    ),
    
    // Create/Edit Employee Modal
    React.createElement(Dialog, { open: showModal, onOpenChange: setShowModal },
      React.createElement(DialogContent, { className: 'max-w-lg' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
            editing ? React.createElement(Edit, { className: 'w-5 h-5' }) : React.createElement(UserPlus, { className: 'w-5 h-5' }),
            editing ? 'Edit Employee' : 'Create New Employee'
          )
        ),
        
        tempPassword 
          ? React.createElement('div', { className: 'py-6 text-center' },
              React.createElement(CheckCircle2, { className: 'w-16 h-16 text-green-600 mx-auto mb-4' }),
              React.createElement('h3', { className: 'text-lg font-semibold mb-2' }, 'Employee Created Successfully!'),
              React.createElement('p', { className: 'text-gray-600 mb-4' }, 'Share the temporary password with the employee:'),
              React.createElement('div', { className: 'bg-slate-100 p-4 rounded-lg flex items-center justify-center gap-2' },
                React.createElement('span', { className: 'font-mono text-lg' }, tempPassword),
                React.createElement(Button, { variant: 'ghost', size: 'sm', onClick: function() { copyToClipboard(tempPassword); } },
                  React.createElement(Copy, { className: 'w-4 h-4' })
                )
              ),
              React.createElement('p', { className: 'text-xs text-gray-500 mt-4' }, 'Employee should change this password after first login.'),
              React.createElement(Button, { onClick: function() { setShowModal(false); setTempPassword(null); }, className: 'mt-4' }, 'Done')
            )
          : React.createElement('div', { className: 'space-y-4 py-4' },
              // Full Name
              React.createElement('div', null,
                React.createElement(Label, null, 'Full Name *'),
                React.createElement(Input, {
                  value: form.full_name,
                  onChange: function(e) { setForm(Object.assign({}, form, { full_name: e.target.value })); },
                  placeholder: 'John Doe',
                  className: 'mt-1'
                })
              ),
              
              // Email & Mobile
              React.createElement('div', { className: 'grid grid-cols-2 gap-4' },
                React.createElement('div', null,
                  React.createElement(Label, null, 'Email *'),
                  React.createElement(Input, {
                    type: 'email',
                    value: form.email,
                    onChange: function(e) { setForm(Object.assign({}, form, { email: e.target.value })); },
                    placeholder: 'john@company.com',
                    className: 'mt-1',
                    disabled: !!editing
                  })
                ),
                React.createElement('div', null,
                  React.createElement(Label, null, 'Mobile'),
                  React.createElement(Input, {
                    value: form.mobile,
                    onChange: function(e) { setForm(Object.assign({}, form, { mobile: e.target.value })); },
                    placeholder: '+91 9876543210',
                    className: 'mt-1'
                  })
                )
              ),
              
              // Department & Role
              React.createElement('div', { className: 'grid grid-cols-2 gap-4' },
                React.createElement('div', null,
                  React.createElement(Label, null, 'Department *'),
                  React.createElement(Select, {
                    value: form.department,
                    onValueChange: function(val) { setForm(Object.assign({}, form, { department: val })); }
                  },
                    React.createElement(SelectTrigger, { className: 'mt-1' },
                      React.createElement(SelectValue, { placeholder: 'Select department' })
                    ),
                    React.createElement(SelectContent, null,
                      departments.map(function(d) {
                        return React.createElement(SelectItem, { key: d, value: d }, d);
                      })
                    )
                  )
                ),
                React.createElement('div', null,
                  React.createElement(Label, null, 'Role *'),
                  React.createElement(Select, {
                    value: form.role,
                    onValueChange: function(val) { setForm(Object.assign({}, form, { role: val })); }
                  },
                    React.createElement(SelectTrigger, { className: 'mt-1' },
                      React.createElement(SelectValue, { placeholder: 'Select role' })
                    ),
                    React.createElement(SelectContent, null,
                      roles.map(function(r) {
                        return React.createElement(SelectItem, { key: r.id, value: r.id }, r.name);
                      })
                    )
                  )
                )
              ),
              
              // Branch & Joining Date
              React.createElement('div', { className: 'grid grid-cols-2 gap-4' },
                React.createElement('div', null,
                  React.createElement(Label, null, 'Branch/Warehouse'),
                  React.createElement(Input, {
                    value: form.branch,
                    onChange: function(e) { setForm(Object.assign({}, form, { branch: e.target.value })); },
                    placeholder: 'Main Warehouse',
                    className: 'mt-1'
                  })
                ),
                React.createElement('div', null,
                  React.createElement(Label, null, 'Joining Date'),
                  React.createElement(Input, {
                    type: 'date',
                    value: form.joining_date,
                    onChange: function(e) { setForm(Object.assign({}, form, { joining_date: e.target.value })); },
                    className: 'mt-1'
                  })
                )
              ),
              
              // Address
              React.createElement('div', null,
                React.createElement(Label, null, 'Address'),
                React.createElement(Textarea, {
                  value: form.address,
                  onChange: function(e) { setForm(Object.assign({}, form, { address: e.target.value })); },
                  placeholder: 'Full address...',
                  rows: 2,
                  className: 'mt-1'
                })
              ),
              
              // Password (only for new employees)
              !editing && React.createElement('div', null,
                React.createElement(Label, null, 'Initial Password (optional)'),
                React.createElement('div', { className: 'relative mt-1' },
                  React.createElement(Input, {
                    type: showPassword ? 'text' : 'password',
                    value: form.password,
                    onChange: function(e) { setForm(Object.assign({}, form, { password: e.target.value })); },
                    placeholder: 'Leave empty to auto-generate'
                  }),
                  React.createElement(Button, {
                    type: 'button',
                    variant: 'ghost',
                    size: 'sm',
                    className: 'absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0',
                    onClick: function() { setShowPassword(!showPassword); }
                  },
                    showPassword ? React.createElement(EyeOff, { className: 'w-4 h-4' }) : React.createElement(Eye, { className: 'w-4 h-4' })
                  )
                ),
                React.createElement('p', { className: 'text-xs text-gray-500 mt-1' }, 'If empty, a secure temporary password will be generated')
              ),
              
              // Role Permissions Info
              form.role && React.createElement('div', { className: 'p-3 bg-blue-50 rounded-lg text-sm' },
                React.createElement('p', { className: 'font-medium text-blue-800 mb-1' }, 
                  'Role: ' + getRoleDisplayName(form.role)
                ),
                React.createElement('p', { className: 'text-blue-600 text-xs' },
                  roles.find(function(r) { return r.id === form.role; })?.description || ''
                )
              )
            ),
        
        !tempPassword && React.createElement(DialogFooter, null,
          React.createElement(Button, { variant: 'outline', onClick: function() { setShowModal(false); } }, 'Cancel'),
          React.createElement(Button, { onClick: saveEmployee, disabled: saving },
            saving 
              ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin mr-2' })
              : null,
            editing ? 'Update Employee' : 'Create Employee'
          )
        )
      )
    )
  );
}

export default EmployeeManagement;
